import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import httpClient from '../../../httpClient';
import './MSTeamsAuthResult.css';

const PROVIDER_LABELS = {
  ms_teams: 'MS Teams',
  sharepoint: 'SharePoint',
};

const PROVIDER_SETTINGS_HINT = {
  ms_teams: 'Return to Calendar Integrations and click Connect to finish linking.',
  sharepoint: 'Return to SharePoint Integration and click Connect to finish linking.',
};

// Backend integration_email_guard: email already backs another org's integration — not retryable.
const EMAIL_IN_USE_ERROR_CODE = 'integration_email_in_use';

const MSTeamsAuthResult = () => {
  const [searchParams] = useSearchParams();
  const isEmailInUse = searchParams.get('error_code') === EMAIL_IN_USE_ERROR_CODE;

  const provider = useMemo(() => {
    const raw = (searchParams.get('provider') || 'ms_teams').toLowerCase();
    return PROVIDER_LABELS[raw] ? raw : 'ms_teams';
  }, [searchParams]);

  const providerLabel = PROVIDER_LABELS[provider];
  const loginEndpoint = provider === 'sharepoint' ? '/ids/integrations/sharepoint/login' : '/ids/integrations/msteams/login';

  const adminConsent = useMemo(() => {
    const flag = searchParams.get('admin_consent');
    return flag && flag.toLowerCase() === 'true';
  }, [searchParams]);

  const { success, title, message, error } = useMemo(() => {
    const authSuccess = searchParams.get('auth_success');
    const isSuccess = authSuccess && authSuccess.toLowerCase() === 'true';
    const errorParam = searchParams.get('error');

    if (adminConsent && isSuccess) {
      return {
        success: true,
        title: 'Admin consent approved',
        message: `Tenant admin has approved access. ${PROVIDER_SETTINGS_HINT[provider]}`,
        error: null,
      };
    }

    if (!adminConsent && isSuccess) {
      return {
        success: true,
        title: `${providerLabel} connected`,
        message: `Your ${providerLabel} account is now linked. You can close this page.`,
        error: null,
      };
    }

    const safeError = errorParam ? decodeURIComponent(errorParam) : `We could not complete ${providerLabel} authentication.`;

    if (isEmailInUse) {
      return { success: false, title: 'Email already in use', message: safeError, error: null };
    }

    if (adminConsent) {
      return {
        success: false,
        title: 'Admin consent failed',
        message: safeError,
        error: safeError,
      };
    }

    return {
      success: false,
      title: `${providerLabel} connection failed`,
      message: safeError,
      error: safeError,
    };
  }, [searchParams, adminConsent, provider, providerLabel, isEmailInUse]);

  return (
    <div className="msteams-result-page">
      <div className="msteams-result-card">
        <div className="msteams-result-header">
          <div className={`msteams-result-icon ${success ? 'success' : 'error'}`}>
            <span className="material-symbols-outlined msteams-result-icon-glyph" aria-hidden="true">
              {success ? 'check_circle' : isEmailInUse ? 'warning' : 'cancel'}
            </span>
          </div>
          <h2 className="msteams-result-title">{title}</h2>
        </div>
        <p className="msteams-result-message">{message}</p>
        <div className="msteams-result-actions">
          {success ? (
            <button className="msteams-result-button" onClick={() => window.close()}>
              Close this page
            </button>
          ) : adminConsent || isEmailInUse ? (
            <button className="msteams-result-button" onClick={() => window.close()}>
              Close
            </button>
          ) : (
            <button
              className="msteams-result-button"
              onClick={async () => {
                try {
                  // Mark the latest integration as failed so UI doesn't show it as connected
                  const token = localStorage.getItem('ids_token');
                  if (token) {
                    try {
                      await httpClient.post(
                        '/ids/integrations/calendar/mark_last_failed',
                        {},
                        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
                      );
                    } catch (e) {
                      // ignore mark failure
                    }
                  }
                  const resp = await httpClient.get(loginEndpoint, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                  });
                  const authUrl = resp.data?.data?.auth_url;
                  if (authUrl) {
                    window.open(authUrl, '_blank');
                  } else {
                    window.location.reload();
                  }
                } catch (err) {
                  window.location.reload();
                }
              }}
            >
              Retry
            </button>
          )}
        </div>
        {success && adminConsent && (
          <div className="msteams-result-hint">{PROVIDER_SETTINGS_HINT[provider]}</div>
        )}
        {!success && error && (
          <div className="msteams-result-hint">
            {adminConsent
              ? 'Admin declined or an error occurred. Please resend the admin consent request.'
              : 'Need help? Go back to integrations and try reconnecting.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default MSTeamsAuthResult;
